import { Reveal } from "./Reveal";
import { Star } from "./Logo";
import { useLang } from "@/i18n/LanguageContext";
import { jsPDF } from "jspdf";
import { Clock, MessageCircle, Sparkles, TrendingUp, Heart, GraduationCap, Download } from "lucide-react";

const ICONS = [Clock, MessageCircle, Sparkles, TrendingUp, Heart, GraduationCap];

const clean = (t) =>
  String(t)
    .replace(/″/g, '"')
    .replace(/[„“”]/g, '"')
    .replace(/[’‘]/g, "'")
    .replace(/–/g, "-")
    .replace(/…/g, "...")
    .replace(/✦/g, "");

const downloadTeamPdf = (d) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595.28;
  const H = 841.89;
  const M = 46;
  const maxW = W - M * 2;
  doc.setFillColor(252, 250, 248);
  doc.rect(0, 0, W, H, "F");
  let y = 52;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(198, 156, 109);
  doc.text("M I R A   M I R R O R", M, y);
  doc.setFontSize(8);
  doc.setTextColor(138, 127, 121);
  doc.text(clean(d.kicker).toUpperCase(), W - M, y, { align: "right" });
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(M, y + 10, W - M, y + 10);
  y += 38;

  doc.setFont("times", "bold");
  doc.setFontSize(20);
  doc.setTextColor(44, 39, 37);
  const title = doc.splitTextToSize(clean(d.title), maxW);
  doc.text(title, M, y);
  y += title.length * 22 + 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(90, 82, 78);
  const sub = doc.splitTextToSize(clean(d.sub), maxW);
  doc.text(sub, M, y);
  y += sub.length * 12 + 20;

  d.args.forEach((a, i) => {
    doc.setFillColor(212, 175, 55);
    doc.circle(M + 7, y - 3, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text(String(i + 1), M + 7, y, { align: "center" });
    doc.setFontSize(11);
    doc.setTextColor(44, 39, 37);
    doc.text(clean(a.t), M + 22, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(90, 82, 78);
    const lines = doc.splitTextToSize(clean(a.d), maxW - 22);
    doc.text(lines, M + 22, y);
    y += lines.length * 11.5 + 15;
  });

  y += 10;
  doc.setFont("times", "bolditalic");
  doc.setFontSize(13);
  doc.setTextColor(198, 156, 109);
  const claim = doc.splitTextToSize("«" + clean(d.claim) + "»", maxW - 40);
  doc.text(claim, W / 2, y, { align: "center" });

  doc.setDrawColor(234, 225, 219);
  doc.line(M, H - 54, W - M, H - 54);
  doc.setFontSize(7.5);
  doc.setTextColor(138, 127, 121);
  doc.text("MYSYMP AG  ·  Studenstrasse 14B, CH-6207 Nottwil  ·  info@mysymp.ch  ·  +41 79 471 06 25  ·  mira-mirror.ai/business", W / 2, H - 38, { align: "center" });

  doc.save("mira-team-onepager.pdf");
};

export default function B2BTeam() {
  const { t } = useLang();
  const d = t.b2bTeam;
  if (!d) return null;

  return (
    <section id="team" className="py-24 sm:py-32" style={{ background: "var(--surface-secondary)" }} data-testid="b2b-team-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <p className="flex items-center justify-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
            <Star size={11} /> {d.kicker}
          </p>
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="b2b-team-title">
            {d.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>
            {d.sub}
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {d.args.map((a, i) => {
            const Icon = ICONS[i];
            return (
              <Reveal key={i} delay={i * 0.06} className="rounded-2xl border p-6" style={{ background: "var(--surface)", borderColor: "var(--line)" }} data-testid={`b2b-team-arg-${i + 1}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: "var(--rose-soft)" }}>
                  <Icon size={19} strokeWidth={1.7} style={{ color: "var(--camel)" }} />
                </span>
                <h3 className="mt-4 text-base font-semibold">{a.t}</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{a.d}</p>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2} className="mt-12 text-center">
          <p className="font-serif-display text-xl sm:text-2xl font-semibold" style={{ color: "var(--camel)" }} data-testid="b2b-team-claim">
            «{d.claim}»
          </p>
          <button
            onClick={() => downloadTeamPdf(d)}
            data-testid="b2b-team-pdf-btn"
            className="pill-btn mt-8 inline-flex items-center gap-2 rounded-full border px-7 py-3.5 text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]"
            style={{ borderColor: "var(--camel)", color: "var(--on-surface)", background: "var(--surface)" }}
          >
            <Download size={15} /> {d.pdfCta}
          </button>
        </Reveal>
      </div>
    </section>
  );
}
